// wordExport.js — turns the finished report into a Word (.docx) file that looks like the PDF and whose
// TEXT CAN BE EDITED in Word.
//
// How it works (the report is drawn by one big jsPDF routine in Configure/index.js):
//   1. While the PDF is being drawn for Word, every pdf.text() AND pdf.addImage() call is RECORDED (page,
//      position, font / size / colour, or the picture itself) instead of being drawn — see
//      createWordTextRecorder(). Skipping the picture embedding also makes building the PDF much faster.
//   2. What is left in the PDF is only simple vector graphics (borders, table fills, lines). Each page of that
//      is rasterised once (pdf.js — fast, because there are no big pictures in it) as the page BACKGROUND.
//   3. Every recorded picture goes back as a floating Word picture at the exact same spot, in its ORIGINAL
//      resolution (big opaque PNGs are only re-encoded as high-quality JPEG to keep the file small).
//   4. Every recorded text goes back on top as a real, editable Word text frame at the exact same spot,
//      with the same font / size / colour.
// So the page looks like the PDF, and the words can be selected, edited, spell-checked, searched.
// (Text that the PDF draws as a picture — e.g. Hindi/Gujarati lines — stays part of the background.)
//
// pdf.js is loaded from cdnjs (same CDN as the floor-plan PDF upload in Uploads.js); docx and file-saver
// are already dependencies and are imported lazily so the normal PDF export is unaffected.

const PDFJS_VERSION = '3.11.174';
const PDFJS_SRC = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.min.js`;
const PDFJS_WORKER_SRC = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.worker.min.js`;

export class WordExportError extends Error {}

let pdfJsPromise = null;
export const loadPdfJs = () => {
  if (typeof window !== 'undefined' && window.pdfjsLib) {
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_SRC;
    return Promise.resolve(window.pdfjsLib);
  }
  if (pdfJsPromise) return pdfJsPromise;
  pdfJsPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = PDFJS_SRC;
    script.onload = () => {
      if (!window.pdfjsLib) { pdfJsPromise = null; reject(new WordExportError('PDF renderer did not initialise.')); return; }
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_SRC;
      resolve(window.pdfjsLib);
    };
    script.onerror = () => {
      pdfJsPromise = null;
      reject(new WordExportError('Could not load the PDF renderer — check your internet connection and try again.'));
    };
    document.head.appendChild(script);
  });
  return pdfJsPromise;
};

// Encodes a canvas: PNG (crisp lines, tiny for flat graphics) unless that gets big, then high-quality JPEG.
const encodeCanvas = async (canvas) => {
  const png = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (png && png.size <= 700000) return { type: 'png', data: new Uint8Array(await png.arrayBuffer()) };
  const jpg = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
  const pick = jpg && (!png || jpg.size < png.size) ? jpg : png;
  if (!pick) throw new WordExportError('Could not convert a page to an image.');
  return { type: pick === jpg ? 'jpg' : 'png', data: new Uint8Array(await pick.arrayBuffer()) };
};

/**
 * Renders every page of a PDF (ArrayBuffer) to a background picture. A few pages are rendered at the same
 * time (each with its own canvas) to cut the waiting time; results keep page order.
 * Pictures are NOT in this PDF (they are placed separately in Word), so the page is only light vector
 * graphics: scale 2.5 on an A4 page = ~1490x2105px (~180 dpi), crisp lines, and still quick to render.
 */
export const pdfToPageBackgrounds = async (pdfArrayBuffer, { scale = 2.5, concurrency = 3, onProgress } = {}) => {
  const pdfjsLib = await loadPdfJs();
  const doc = await pdfjsLib.getDocument({ data: pdfArrayBuffer }).promise;
  const total = doc.numPages;
  const results = new Array(total);
  let next = 1;
  let done = 0;

  const worker = async () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    for (;;) {
      const i = next++;
      if (i > total) break;
      // eslint-disable-next-line no-await-in-loop
      const page = await doc.getPage(i);
      const viewport = page.getViewport({ scale });
      canvas.width = Math.round(viewport.width);
      canvas.height = Math.round(viewport.height);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      // eslint-disable-next-line no-await-in-loop
      await page.render({ canvasContext: ctx, viewport }).promise;
      // eslint-disable-next-line no-await-in-loop
      results[i - 1] = await encodeCanvas(canvas);
      page.cleanup();
      done += 1;
      if (onProgress) onProgress(done, total);
    }
    canvas.width = 0; canvas.height = 0; // release the bitmap
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, total) }, worker));
  doc.destroy();
  return results;
};

// ───────────────────────── recording the PDF's text ─────────────────────────

const PT_TO_MM = 25.4 / 72;

/**
 * Hooks pdf.text() of a jsPDF instance: plain text is recorded into `ops` (and NOT drawn), everything
 * jsPDF cannot describe simply (rotated text, odd argument shapes) is drawn as usual so it still ends up
 * in the page background. Works for jspdf-autotable too, which draws its cell text through doc.text().
 */
export const createWordTextRecorder = (pdf) => {
  const ops = [];
  const images = [];          // recorded pictures: { page, data (data URL), x, y, w, h } in mm
  const origText = pdf.text.bind(pdf);
  let translucent = false;    // inside a semi-transparent graphics state (see setGState below)

  pdf.text = function recordedText(text, x, y, options, transform) {
    try {
      const opt = (options && typeof options === 'object') ? options : {};
      const simple = typeof x === 'number' && typeof y === 'number' && !transform && !opt.angle
        && !opt.rotationDirection && text !== undefined && text !== null && !opt.renderingMode;
      if (!simple) return origText(text, x, y, options, transform);

      let lines = Array.isArray(text) ? text.map((t) => String(t)) : [String(text)];
      if (opt.maxWidth) lines = lines.flatMap((l) => pdf.splitTextToSize(l, opt.maxWidth));

      const size = pdf.getFontSize();                       // pt
      const font = pdf.getFont();
      const scaleFactor = pdf.internal.scaleFactor;         // pt per unit (mm)
      const lineH = ((opt.lineHeightFactor || pdf.getLineHeightFactor()) * size) / scaleFactor; // mm
      const page = pdf.internal.getCurrentPageInfo().pageNumber;
      const color = pdf.getTextColor();

      // jsPDF y is the text BASELINE unless a baseline option moves it.
      let baselineY = y;
      const sizeMm = size * PT_TO_MM;
      if (opt.baseline === 'top' || opt.baseline === 'hanging') baselineY = y + sizeMm * 0.8;
      else if (opt.baseline === 'middle') baselineY = y + sizeMm * 0.35;
      else if (opt.baseline === 'bottom' || opt.baseline === 'ideographic') baselineY = y - sizeMm * 0.2;

      lines.forEach((ln, i) => {
        if (!String(ln).trim()) return;
        ops.push({
          page,
          text: String(ln),
          x,
          y: baselineY + i * lineH,
          size,
          font: font.fontName,
          style: font.fontStyle,
          color,
          align: opt.align || 'left',
          width: pdf.getTextWidth(String(ln)),               // mm, with the current font/size
        });
      });
      return pdf;
    } catch (e) {
      return origText(text, x, y, options, transform);
    }
  };

  // Pictures: recorded and NOT embedded in the PDF (embedding big PNGs is the slow part of building it, and
  // re-rasterising them is what made them blurry). Anything unusual (rotated, translucent, not a data URL)
  // is drawn into the PDF as before, so it still ends up in the page background.
  const origAddImage = pdf.addImage.bind(pdf);
  pdf.addImage = function recordedAddImage(...args) {
    try {
      const [imageData, , x, y, w, h, , , rotation] = args;
      const simple = typeof imageData === 'string' && /^data:image\/(png|jpe?g|webp|gif);base64,/i.test(imageData)
        && [x, y, w, h].every((n) => typeof n === 'number' && Number.isFinite(n)) && w > 0 && h > 0
        && !rotation && !translucent;
      if (!simple) return origAddImage(...args);
      images.push({ page: pdf.internal.getCurrentPageInfo().pageNumber, data: imageData, x, y, w, h });
      return pdf;
    } catch (e) {
      return origAddImage(...args);
    }
  };

  // A text that is later fully covered by an opaque filled rectangle (the PDF "paints over" it) must not come
  // back on top in Word, so drop recorded texts that end up completely inside such a rectangle.
  const origSetGState = pdf.setGState && pdf.setGState.bind(pdf);
  if (origSetGState) {
    pdf.setGState = function (g) {
      try { if (g && typeof g.opacity === 'number') translucent = g.opacity < 0.999; } catch (e) { /* ignore */ }
      return origSetGState(g);
    };
  }
  const origRestore = pdf.restoreGraphicsState && pdf.restoreGraphicsState.bind(pdf);
  if (origRestore) pdf.restoreGraphicsState = function () { translucent = false; return origRestore(); };

  const coverTexts = (rx, ry, rw, rh) => {
    const page = pdf.internal.getCurrentPageInfo().pageNumber;
    for (let i = ops.length - 1; i >= 0; i--) {
      const o = ops[i];
      if (o.page !== page) continue;
      const sizeMm = o.size * PT_TO_MM;
      const top = o.y - sizeMm * 0.8;
      const bottom = o.y + sizeMm * 0.25;
      const left = o.align === 'center' ? o.x - o.width / 2 : (o.align === 'right' ? o.x - o.width : o.x);
      const right = left + o.width;
      if (left >= rx - 0.5 && right <= rx + rw + 0.5 && top >= ry - 0.5 && bottom <= ry + rh + 0.5) ops.splice(i, 1);
    }
  };
  ['rect', 'roundedRect'].forEach((name) => {
    if (typeof pdf[name] !== 'function') return;
    const orig = pdf[name].bind(pdf);
    pdf[name] = function (...args) {
      const r = orig(...args);
      try {
        const style = args[args.length - 1];
        const [x, y, w, h] = args;
        if (!translucent && typeof style === 'string' && /f/i.test(style) && [x, y, w, h].every((n) => typeof n === 'number')) coverTexts(x, y, w, h);
      } catch (e) { /* ignore */ }
      return r;
    };
  });

  return { ops, images, restore: () => { pdf.text = origText; pdf.addImage = origAddImage; } };
};

// ───────────────────────────── pictures for Word ─────────────────────────────

// Data URL -> bytes. fetch() decodes it off the main thread; atob is the fallback.
const dataUrlToBytes = async (dataUrl) => {
  try {
    const res = await fetch(dataUrl);
    return new Uint8Array(await res.arrayBuffer());
  } catch (e) {
    const bin = atob(dataUrl.slice(dataUrl.indexOf(',') + 1));
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  }
};

const loadImageElement = (src) => new Promise((resolve, reject) => {
  const im = new Image();
  im.onload = () => resolve(im);
  im.onerror = () => reject(new WordExportError('A picture of the report could not be read.'));
  im.src = src;
});

/**
 * Picture as Word wants it: the ORIGINAL bytes, untouched — PNG and JPEG go in exactly as the report made
 * them (full resolution, nothing re-encoded, so nothing to wait for and nothing lost). Only formats Word
 * cannot use (webp, gif) are converted to PNG.
 */
const prepareImage = async (dataUrl) => {
  const mime = ((/^data:image\/([a-z0-9+.-]+)/i.exec(dataUrl) || [])[1] || '').toLowerCase();
  if (mime === 'jpeg' || mime === 'jpg') return { type: 'jpg', data: await dataUrlToBytes(dataUrl) };
  if (mime === 'png') return { type: 'png', data: await dataUrlToBytes(dataUrl) };
  const im = await loadImageElement(dataUrl);
  const canvas = document.createElement('canvas');
  canvas.width = im.naturalWidth; canvas.height = im.naturalHeight;
  canvas.getContext('2d').drawImage(im, 0, 0);
  const png = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  canvas.width = 0; canvas.height = 0;
  return { type: 'png', data: new Uint8Array(await png.arrayBuffer()) };
};

/** Prepares every distinct picture once (a few at a time) and returns a Map(dataUrl -> prepared). */
const prepareAllImages = async (imageOps, concurrency = 4) => {
  const unique = Array.from(new Set(imageOps.map((o) => o.data)));
  const prepared = new Map();
  let next = 0;
  const worker = async () => {
    for (;;) {
      const i = next++;
      if (i >= unique.length) break;
      // eslint-disable-next-line no-await-in-loop
      prepared.set(unique[i], await prepareImage(unique[i]));
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, unique.length) }, worker));
  return prepared;
};

// ───────────────────────────── building the .docx ─────────────────────────────

// A4 in twentieths of a point (what Word measures pages in) and in CSS pixels (what docx sizes pictures in).
const A4_W_TWIP = 11906;
const A4_H_TWIP = 16838;
const A4_W_PX = 794;
const A4_H_PX = 1123;
const MM_TO_TWIP = 56.692913;
// Where the text baseline sits inside a Word line of exact height (fraction of the line height from the top).
const BASELINE_IN_LINE = 0.8;
const LINE_FACTOR = 1.2;

const mapFont = (name) => {
  const n = String(name || '').toLowerCase();
  if (n === 'helvetica' || n === 'arial') return 'Arial';
  if (n === 'times' || n === 'times-roman') return 'Times New Roman';
  if (n === 'courier') return 'Courier New';
  return name || 'Arial';
};

const hexColor = (c) => {
  if (typeof c === 'string') {
    const m6 = c.match(/^#?([0-9a-f]{6})$/i);
    if (m6) return m6[1].toUpperCase();
    const m3 = c.match(/^#?([0-9a-f]{3})$/i);
    if (m3) return m3[1].split('').map((ch) => ch + ch).join('').toUpperCase();
  }
  return '000000';
};

/**
 * pages: [{ bg: { type, data }, images: [{ type, data, x, y, w, h }], texts: [recorded text op, ...] }, ...]
 * Each page = one A4 section, zero margins: the background floats behind, the pictures float above it, the
 * texts are absolutely positioned (frame) paragraphs on top of everything. Returns a Blob (.docx).
 */
export const buildDocxFromPages = async (pages, { title = 'Vastu Report' } = {}) => {
  if (!pages || !pages.length) throw new WordExportError('There are no pages to put in the Word file.');
  const {
    Document, Packer, Paragraph, TextRun, ImageRun, LineRuleType, AlignmentType,
    HorizontalPositionRelativeFrom, VerticalPositionRelativeFrom, TextWrappingType,
    FrameAnchorType, HeightRule, FrameWrap,
  } = await import('docx');

  const sections = pages.map((pg, idx) => {
    const children = [];

    (pg.texts || []).forEach((t) => {
      const widthMm = t.width || (t.text.length * t.size * PT_TO_MM * 0.5);
      // A little wider than the text so Word never wraps it onto a second line. Centred / right-aligned
      // text sits in a frame centred on / ending at the same x, with a matching paragraph alignment, so
      // small font-width differences between the PDF and Word never shift it.
      const frameWmm = widthMm * 1.08 + 120 / MM_TO_TWIP;
      let leftMm = t.x;
      let alignment = AlignmentType.LEFT;
      if (t.align === 'center') { leftMm = t.x - frameWmm / 2; alignment = AlignmentType.CENTER; }
      else if (t.align === 'right') { leftMm = t.x - frameWmm; alignment = AlignmentType.RIGHT; }
      const lineTw = Math.round(t.size * LINE_FACTOR * 20);
      const topMm = t.y - ((t.size * LINE_FACTOR * BASELINE_IN_LINE) * PT_TO_MM);
      const bold = /bold/i.test(t.style || '');
      const italics = /italic|oblique/i.test(t.style || '');
      children.push(new Paragraph({
        frame: {
          type: 'absolute',
          position: { x: Math.max(0, Math.round(leftMm * MM_TO_TWIP)), y: Math.max(0, Math.round(topMm * MM_TO_TWIP)) },
          width: Math.round(frameWmm * MM_TO_TWIP),
          height: lineTw,
          rule: HeightRule.EXACT,
          wrap: FrameWrap.NONE,
          anchor: { horizontal: FrameAnchorType.PAGE, vertical: FrameAnchorType.PAGE },
        },
        alignment,
        spacing: { before: 0, after: 0, line: lineTw, lineRule: LineRuleType.EXACT },
        children: [new TextRun({
          text: t.text,
          font: mapFont(t.font),
          size: Math.max(2, Math.round(t.size * 2)),
          bold,
          italics,
          color: hexColor(t.color),
        })],
      }));
    });

    // The pictures go in the LAST (normal-flow) paragraph of the section, anchored to the page: first the
    // background (lowest layer), then each recorded picture above it, all behind the text.
    // z-order of the behind-text pictures: a HIGHER relativeHeight is drawn in front (checked in real Word; tiny
    // values like 1 and 2 behave oddly, so use the large numbers Word itself writes). Background lowest,
    // every recorded picture above it, in the order the report drew them.
    const BG_Z = 251658240;
    const IMG_Z = 251658241;
    const MM_TO_EMU = 36000;
    const MM_TO_PX = 96 / 25.4;
    const float = (xEmu, yEmu, z) => ({
      horizontalPosition: { relative: HorizontalPositionRelativeFrom.PAGE, offset: xEmu },
      verticalPosition: { relative: VerticalPositionRelativeFrom.PAGE, offset: yEmu },
      behindDocument: true,
      allowOverlap: true,
      lockAnchor: true,
      wrap: { type: TextWrappingType.NONE },
      zIndex: z,
    });
    const runs = [new ImageRun({
      type: pg.bg.type,
      data: pg.bg.data,
      transformation: { width: A4_W_PX, height: A4_H_PX },
      floating: float(0, 0, BG_Z),
      altText: { name: `Page ${idx + 1}`, description: `Report page ${idx + 1}`, title: `Page ${idx + 1}` },
    })];
    (pg.images || []).forEach((im, k) => {
      runs.push(new ImageRun({
        type: im.type,
        data: im.data,
        transformation: { width: Math.max(1, Math.round(im.w * MM_TO_PX)), height: Math.max(1, Math.round(im.h * MM_TO_PX)) },
        floating: float(Math.round(im.x * MM_TO_EMU), Math.round(im.y * MM_TO_EMU), IMG_Z + k),
        altText: { name: `Picture ${k + 1}`, description: `Picture ${k + 1} on page ${idx + 1}`, title: `Picture ${k + 1}` },
      }));
    });
    children.push(new Paragraph({
      spacing: { before: 0, after: 0, line: 20, lineRule: LineRuleType.EXACT },
      children: runs,
    }));

    return {
      properties: {
        page: {
          size: { width: A4_W_TWIP, height: A4_H_TWIP },
          margin: { top: 0, right: 0, bottom: 0, left: 0, header: 0, footer: 0, gutter: 0 },
        },
      },
      children,
    };
  });

  const doc = new Document({ creator: 'Vastu Analyzer', title, sections });
  try {
    const zip = Packer.compiler.compile(doc);
    Object.keys(zip.files).forEach((name) => {
      if (/^word\/media\//i.test(name) && zip.files[name] && zip.files[name].options) zip.files[name].options.compression = 'STORE';
    });
    return await zip.generateAsync({
      type: 'blob',
      mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      compression: 'DEFLATE',
    });
  } catch (e) {
    return Packer.toBlob(doc);   // same file, just without the speed-up
  }
};

/**
 * Full pipeline: finished PDF bytes (text already left out) + the recorded texts → editable .docx Blob.
 * `texts` = recorder.ops. Without it the pages are simply pictures (not editable).
 */
export const pdfToDocxBlob = async (pdfArrayBuffer, options = {}) => {
  const { texts = [], images = [], timings, ...rest } = options;   // timings: optional object, filled with ms per phase
  const t0 = performance.now();
  // the pictures are prepared while the background pages are being rasterised
  const [backgrounds, prepared] = await Promise.all([
    (async () => { const r = await pdfToPageBackgrounds(pdfArrayBuffer, rest); if (timings) timings.backgroundsMs = Math.round(performance.now() - t0); return r; })(),
    (async () => { const r = await prepareAllImages(images); if (timings) timings.picturesMs = Math.round(performance.now() - t0); return r; })(),
  ]);
  const t1 = performance.now();
  const textsByPage = new Map();
  texts.forEach((t) => {
    if (!textsByPage.has(t.page)) textsByPage.set(t.page, []);
    textsByPage.get(t.page).push(t);
  });
  const imagesByPage = new Map();
  images.forEach((im) => {
    const p = prepared.get(im.data);
    if (!p) return;
    if (!imagesByPage.has(im.page)) imagesByPage.set(im.page, []);
    imagesByPage.get(im.page).push({ type: p.type, data: p.data, x: im.x, y: im.y, w: im.w, h: im.h });
  });
  const pages = backgrounds.map((bg, i) => ({ bg, images: imagesByPage.get(i + 1) || [], texts: textsByPage.get(i + 1) || [] }));
  const blob = await buildDocxFromPages(pages, rest);
  if (timings) timings.packMs = Math.round(performance.now() - t1);
  return blob;
};

export const downloadBlob = async (blob, fileName) => {
  try {
    const mod = await import('file-saver');
    const saveAs = mod.saveAs || mod.default;
    saveAs(blob, fileName);
  } catch (e) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = fileName;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  }
};
